// import utils from '@/lib/utils'
import _ from 'lodash'
import api from '@/lib/api'
import store from '@/store'
import moment from 'moment'
import { ConfigItemDataType } from '@/lib/enums'

const nameRegex = /^\w+$/
const namespaceRegex = /^(\w[\w/]*\/)?$/
const fullNameRegex = /^\w([\w/]*\w)?$/

// --修正数据库配置项数据
// select substring(full_name, 1, length(full_name) - length(name)) computedNamespace, namespace, full_name, name from sys_config_item
// update sys_config_item set namespace = substring(full_name, 1, length(full_name) - length(name))

export function validateConfigItem (item) {
  if (!item) {
    console.warn('配置项为空')
    return false
  }
  const { fullName, name, namespace } = item
  if (!fullNameRegex.test(fullName) ||
    !namespaceRegex.test(namespace) ||
    !nameRegex.test(name) ||
    fullName !== ((namespace ?? '') + name)) {
    console.warn('非法的配置项名称:', item)
    return false
  }
  return true
}

export function parseConfigItemValue ({ value, dataType }) {
  if (_.isNil(value)) return null
  dataType = dataType ?? ConfigItemDataType.String
  if (!value) return dataType === ConfigItemDataType.String ? value : null
  switch (dataType) {
    case ConfigItemDataType.String:
      return value
    case ConfigItemDataType.Integer:
      return parseInt(value)
    case ConfigItemDataType.Float:
      return parseFloat(value)
    case ConfigItemDataType.DateTime:
    {
      const datetime = moment(value)
      return datetime.isValid() ? datetime : null
    }
    case ConfigItemDataType.Object:
      try {
        return JSON.parse(value)
      } catch (e) {
        console.error('解析配置项JSON出错', value, e)
        return null
      }
    default:
      console.log('未知的config dataType', dataType, value)
      return value
  }
}

export function stringifyConfigItemValue ({ value, dataType }) {
  if (_.isNil(value)) return null
  dataType = dataType ?? ConfigItemDataType.String
  switch (dataType) {
    case ConfigItemDataType.String:
    case ConfigItemDataType.Integer:
    case ConfigItemDataType.Float:
      return String(value)
    case ConfigItemDataType.DateTime:
    {
      const datetime = moment(value)
      return datetime.toISOString(true)
    }
    case ConfigItemDataType.Object:
      return JSON.stringify(value)
    default:
      console.log('未知的config dataType', dataType, value)
      return String(value)
  }
}

export function buildConfigItem (item, config) {
  if (!validateConfigItem(item)) return
  let configItemObj = config
  if (item.namespace) {
    const names = item.namespace.split('/').map(item => _.camelCase(item))
    names.pop()
    for (const name of names) {
      let nextObj = configItemObj[name]
      if (!nextObj || !(nextObj instanceof Object)) {
        nextObj = { }
        configItemObj[name] = nextObj
      }
      configItemObj = nextObj
    }
  }
  configItemObj[_.camelCase(item.name)] = parseConfigItemValue(item)
}

export function buildConfig (items) {
  const config = { }
  for (const item of items) {
    buildConfigItem(item, config)
  }
  return config
}

const defaultScopeTypes = ['app', 'terminal', 'user']
const terminalScopeTypes = ['app', 'terminal']

function getScopeToken (scopeType) {
  let scopeToken
  switch (scopeType) {
    case 'app':
      scopeToken = store.state?.bis?.authorize?.app?.id
      break
    case 'terminal':
      scopeToken = store.state?.bis?.authorize?.terminal?.id
      break
    case 'user':
      scopeToken = store.state?.bis?.authorize?.user?.id
      break
    default:
      throw new Error('未知的scopeType:' + scopeType)
  }
  return _.isNil(scopeToken) ? null : String(scopeToken)
}

function getLevel (scopeType) {
  switch (scopeType) {
    case 'app':
      return 100
    case 'terminal_group':
      return 200
    case 'user_group':
      return 300
    case 'terminal':
      return 400
    case 'user':
      return 500
    default:
      throw new Error('未知的scopeType:' + scopeType)
  }
}

export async function getProfileItemsObject ({
  scopeTypes = defaultScopeTypes,
  fullNames
}) {
  const scopes = (scopeTypes ?? defaultScopeTypes).map(scopeType => ({
    scopeType,
    scopeToken: getScopeToken(scopeType)
  }))
  const searchParams = {
    merge: true,
    scopes: scopes,
    fullNames: fullNames
  }
  const searchResult = await api.bis.post('/sysProfileItem/search', searchParams)
  searchResult.notifyIfFail()
  if (searchResult.success) {
    const items = searchResult.data ?? [] // data即items数组
    const fullNamesSet = fullNames ? new Set(fullNames) : null
    const result = {}
    for (const item of items) {
      if (fullNamesSet && !fullNamesSet.has(item.fullName)) {
        console.warn('意外的profile查询结果', item, searchResult, searchParams)
        continue
      }
      if (result[item.fullName]) {
        console.warn('多余的profile查询结果', item, searchResult, searchParams)
        continue
      }
      result[item.fullName] = {
        ...item,
        value: parseConfigItemValue(item)
      }
    }
    return result
  } else {
    return null
  }
}

export async function getProfileItem ({
  scopeTypes = defaultScopeTypes,
  fullName
}) {
  const profileItemsObject = await getProfileItemsObject({ scopeTypes, fullNames: [fullName] })
  return profileItemsObject ? profileItemsObject[fullName] : null
}

export async function getProfileValue ({
  scopeTypes = defaultScopeTypes,
  fullName
}) {
  const profileItemsObject = await getProfileItemsObject({ scopeTypes, fullNames: [fullName] })
  return profileItemsObject ? profileItemsObject[fullName]?.value : null
}

export async function setProfileItems ({
  scopeTypes = defaultScopeTypes,
  items // [{fullName, dataType, value}]
}) {
  const fullNames = items.map(item => item.fullName)
  const profileItemsObject = await getProfileItemsObject({ scopeTypes, fullNames })
  if (!profileItemsObject) {
    return 0
  }
  let successCount = 0
  for (const item of items) {
    const currItem = profileItemsObject[item.fullName]
    if (currItem) {
      const putResult = await api.bis.put(`/sysProfileItem/${encodeURIComponent(currItem.id)}`, {
        ...currItem,
        dataType: item.dataType,
        value: stringifyConfigItemValue({ value: item.value, dataType: item.dataType })
      })
      putResult.notifyIfFail()
      successCount += putResult.success ? 1 : 0
    } else {
      const selectedScopeType = _.maxBy(scopeTypes, st => getLevel(st))
      const postResult = await api.bis.post('/sysProfileItem/', {
        ...resolveConfigFullName(item.fullName),
        fullName: item.fullName,
        scopeType: selectedScopeType,
        scopeToken: getScopeToken(selectedScopeType),
        dataType: item.dataType,
        level: getLevel(selectedScopeType),
        value: stringifyConfigItemValue({ value: item.value, dataType: item.dataType })
      })
      postResult.notifyIfFail()
      successCount += postResult.success ? 1 : 0
    }
  }
  return successCount
}

function resolveConfigFullName (fullName) {
  if (!fullName) return { namespace: '', name: '' }
  const lastIndex = fullName.lastIndexOf('/')
  return {
    namespace: fullName.substr(0, lastIndex + 1),
    name: fullName.substr(lastIndex + 1)
  }
}

export function getProfilePickUsers (pickUsersKey) {
  return getProfileValue({
    scopeTypes: terminalScopeTypes,
    fullName: '/pick-users/' + encodeURIComponent(pickUsersKey)
  })
}

export function setProfilePickUsers (pickUsersKey, pickUsers) {
  return setProfileItems({
    scopeTypes: terminalScopeTypes,
    items: [{
      fullName: '/pick-users/' + encodeURIComponent(pickUsersKey),
      dataType: ConfigItemDataType.Object,
      value: pickUsers
    }]
  })
}

export function getProfileFieldsDefaults (fieldsDefaultsKey) {
  return getProfileValue({
    scopeTypes: terminalScopeTypes,
    fullName: '/fields-defaults/' + encodeURIComponent(fieldsDefaultsKey)
  })
}

export function setProfileFieldsDefaults (fieldsDefaultsKey, fieldsDefaults) {
  return setProfileItems({
    scopeTypes: terminalScopeTypes,
    items: [{
      fullName: '/fields-defaults/' + encodeURIComponent(fieldsDefaultsKey),
      dataType: ConfigItemDataType.Object,
      value: fieldsDefaults
    }]
  })
}

export async function getProfileDocPrintOptions (printOptionsKey) {
  const text = await getProfileValue({
    scopeTypes: terminalScopeTypes,
    fullName: '/doc-print-options/' + encodeURIComponent(printOptionsKey)
  })
  return JSON.parse(text || '{}')
}

export function setProfileDocPrintOptions (printOptionsKey, printOptions) {
  return setProfileItems({
    scopeTypes: terminalScopeTypes,
    items: [{
      fullName: '/doc-print-options/' + encodeURIComponent(printOptionsKey),
      dataType: ConfigItemDataType.Object,
      value: printOptions
    }]
  })
}
